import React, { useEffect, useState } from 'react';
import { useParams, Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import Select from 'react-select';

import CommonAnimatedLoader from '../../components/common/animated/loader';
import { useOrder } from '../../hooks/use-order';
import * as usersService from '../../services/api/users';
import * as ordersService from '../../services/api/orders';
import * as testsService from '../../services/api/tests';
import { useAuth } from '../../hooks/use-auth';
import OrderStatusBadge from '../../components/orders/status-badge';
import { OrderStatus } from '../../entities/order';
import CommonAnimatedSpinner from '../../components/common/animated/spinner';
import CommonConfirmationModal from '../../components/modals/confirmation';
import { Allergist } from '../../entities/form/allergist';
import CommonButtonDefault from '../../components/common/buttons/default';
import StyledError from '../../components/common/form/styled-error';
import IsAsitStatusBadge from '../../components/orders/is-asit-badge';
import EditEmailModal from '../../components/order/edit-email-modal';
import PatientManagementModal from '../../components/patients/PatientManagementModal';
import { buildShopifyOnboardingUrl, copyToClipboard } from '../../utils/shopify';

function OrdersResultsPageComponent() {
  const { id } = useParams();
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSavingStatus, setIsSavingStatus] = useState<boolean>(false);
  const [isSavingMarkAsit, setIsSavingMarkAsit] = useState<boolean>(false);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [isMarking, setIsMarking] = useState<boolean>(false);
  const [isReverting, setIsReverting] = useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [doctors, setDoctors] = useState<Allergist[]>();
  const [error, setError] = useState<string>();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [isPatientModalVisible, setIsPatientModalVisible] = useState<boolean>(false);
  const [isSendingToThriva, setIsSendingToThriva] = useState<boolean>(false);
  const [isResendingEmail, setIsResendingEmail] = useState<boolean>(false);
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const [isRegeneratingPdf, setIsRegeneratingPdf] = useState<boolean>(false);
  const location = useLocation();
  const { setOrderId, updateOrder, order } = useOrder();

  const allTabs = [
    { name: 'Order details', href: `/orders/results/${id}`, current: true },
    { name: 'Questionnaire', href: `/orders/results/${id}/questionnaire`, current: false },
    { name: 'Blood results', href: `/orders/results/${id}/blood`, current: false },
    { name: 'Allergens', href: `/orders/results/${id}/answer`, current: false },
    { name: 'Comments', href: `/orders/results/${id}/comments`, current: false },
    { name: 'Recommendations', href: `/orders/results/${id}/recommendations`, current: false },
  ];

  const tabs = order?.order.productIsTest ? [allTabs[0]] : allTabs;

  for (const tab of tabs) {
    tab.current = tab.href === location.pathname;
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const allergists = await usersService.getAdministratorsList(token, { showInAdmin: 1 });

        if (allergists) {
          setDoctors(allergists);
        }
      } catch (e) {
        setError((e as Error).message);
      }
    };

    if (id) {
      setOrderId(parseInt(id, 10));
      fetchData();
      setIsLoading(false);
    }
  }, [token, id, setOrderId]);

  const onCompleteOrder = async () => {
    setIsConfirming(false);
    setIsSavingStatus(true);
    if (order) {
      await ordersService.complete(token, order?.order.id);

      updateOrder({
        ...order.order,
        status: OrderStatus.Completed,
      });
    }
    setIsSavingStatus(false);
  };

  const onMarkAsitOrder = async () => {
    setIsMarking(false);
    setIsSavingMarkAsit(true);
    if (order) {
      const updatedOrder = await ordersService.toggleAsit(token, order.order.id, order.order.isAsit);

      updateOrder({
        ...order.order,
        isAsit: updatedOrder.isAsit, // Use the actual toggled value from backend
      });
    }
    setIsSavingMarkAsit(false);
  };

  const onRevertOrder = async () => {
    setIsReverting(false);
    setIsSavingStatus(true);
    try {
      if (order) {
        await ordersService.update(token, order.order.id, { status: OrderStatus.SampleReceived });

        updateOrder({
          ...order.order,
          status: OrderStatus.SampleReceived,
        });
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsSavingStatus(false);
    }
  };

  const onCancelOrder = async () => {
    setIsCancelling(false);
    setIsSavingStatus(true);
    try {
      if (order && order.tests && order.tests.length > 0) {
        await testsService.cancel(token, order.tests[0].id);
        updateOrder({
          ...order.order,
          status: OrderStatus.Cancelled,
        });
      } else if (order) {
        await ordersService.update(token, order.order.id, { status: OrderStatus.Cancelled });
        updateOrder({
          ...order.order,
          status: OrderStatus.Cancelled,
        });
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsSavingStatus(false);
    }
  };

  const onSelectDoctor = async (allergist: Allergist) => {
    if (id && allergist.id) {
      await ordersService.update(token, parseInt(id, 10), { administratorId: allergist.id });
    } else {
      setError('Allergist id is missing');
    }
  };

  const onSelectPreview = async () => {
    if (order) {
      const url = order?.frontendUrl || 'https://app.allergomedica.lt';

      if (url) {
        window.open(`${url}/results/redirect/${order.order.hash}/${token}`, '_blank');
      }
    } else {
      setIsLoading(false);
      setError('Order id or is missing');
    }
  };

  const downloadPdf = async () => {
    if (order?.order.pdfId) {
      try {
        setDisabled(true);
        const res = await ordersService.getPdf(token, order.order.pdfId);

        if (res) {
          setDisabled(false);
          window.location.href = res;
        }
      } catch (e) {
        setError((e as Error).message);
      }
    }
  };

  const onUpdateEmail = (email: string) => {
    if (order) {
      updateOrder({ ...order.order, email });
    }
  };

  const onOpenPatientModal = () => {
    setIsPatientModalVisible(true);
  };

  const onClosePatientModal = () => {
    setIsPatientModalVisible(false);
  };

  const onPatientAssigned = () => {
    setIsPatientModalVisible(false);
  };

  const onSendToThriva = async () => {
    if (!order) return;

    setIsSendingToThriva(true);
    setError(undefined);

    try {
      const result = await ordersService.sendToThriva(token, order.order.id);

      updateOrder({ ...order.order, thrivaOrderId: result.thrivaOrderId });
    } catch (e) {
      setError((e as Error).message || 'Failed to send to Thriva');
    } finally {
      setIsSendingToThriva(false);
    }
  };

  const onResendConfirmation = async () => {
    if (!order) return;

    setIsResendingEmail(true);
    setError(undefined);

    try {
      await ordersService.resendConfirmation(token, order.order.id, order.order.email);
    } catch (e) {
      setError((e as Error).message || 'Failed to resend confirmation email');
    } finally {
      setIsResendingEmail(false);
    }
  };

  const onRegeneratePdf = async () => {
    if (!order) return;

    setIsRegeneratingPdf(true);
    setError(undefined);

    try {
      await ordersService.regeneratePdf(token, order.order.hash);
      updateOrder({ ...order.order, pdfId: undefined, pdfIds: undefined });
    } catch (e) {
      setError((e as Error).message || 'Failed to regenerate PDF');
    } finally {
      setIsRegeneratingPdf(false);
    }
  };

  const onCopyOnboardingLink = async () => {
    if (!order?.order.shopifyOrderId) return;

    const url = buildShopifyOnboardingUrl(order.order.shopifyOrderId, order.order.email, order.order.region || 'eu');
    const success = await copyToClipboard(url);

    if (success) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  return (
    <div>
      {(isLoading || order === undefined) && <CommonAnimatedLoader />}
      {error && <StyledError title={error} />}
      {order && (
        <div>
          <div className="pb-5 pt-5 bg-white border-b border-gray-200 sm:pb-0">
            <div className="px-4 sm:px-6 lg:px-8">
              {/* Header row */}
              <div className="flex flex-col gap-2">
                <h3 className="font-medium text-gray-900">
                  Buyer:{' '}
                  {order.order.researchMemberId
                    ? `DI${order.order.researchMemberId}`
                    : `${order.order.firstName} ${order.order.lastName}`}{' '}
                  (Order ID: {order.order.id})
                </h3>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200">
                    {order.order.productKey}
                    {order.order.isConsultation && order.order.productKey !== 'CONSULTATION' && ' + Consultation'}
                  </span>
                  <span className="text-sm text-gray-600">Tel: {order.order.phone}</span>
                  <span className="text-sm text-gray-600">Email: {order.order.email}</span>
                  {order && order.order.customerId && (
                    <EditEmailModal
                      currEmail={order.order.email}
                      userId={order.order.customerId}
                      onUpdateEmail={(email) => onUpdateEmail(email)}
                    />
                  )}
                </div>
              </div>

              {/* Action bar */}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {/* Status section */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Status:</span>
                  <OrderStatusBadge status={order.order.status} />
                  {isSavingStatus && <CommonAnimatedSpinner sm />}
                </div>

                <div className="flex items-center gap-2">
                  <IsAsitStatusBadge status={order.order.isAsit} />
                  {isSavingMarkAsit && <CommonAnimatedSpinner sm />}
                </div>

                {order.order.thrivaOrderId && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    <span>Fulfilled by Thriva</span>
                    <span className="font-mono text-purple-600">{order.order.thrivaOrderId}</span>
                  </span>
                )}

                {/* Spacer */}
                <div className="flex-1" />

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={onOpenPatientModal}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Manage Patient
                  </button>

                  {order.order.region === 'uk' && !order.order.thrivaOrderId && (
                    <button
                      type="button"
                      onClick={onSendToThriva}
                      disabled={isSendingToThriva}
                      className="inline-flex items-center px-3 py-1.5 border border-purple-300 rounded-md text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-50"
                    >
                      {isSendingToThriva ? 'Sending...' : 'Send to Thriva'}
                    </button>
                  )}

                  {order.order.customerId && (
                    <Link
                      to={`/messages/${order.order.customerId}`}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 no-underline"
                    >
                      Chat
                    </Link>
                  )}

                  {order.order.productKey !== 'TREATMENT_PLAN' && (
                    <button
                      type="button"
                      onClick={onSelectPreview}
                      className="inline-flex items-center px-3 py-1.5 border border-indigo-300 rounded-md text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                    >
                      Preview
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={onResendConfirmation}
                    disabled={isResendingEmail}
                    className="inline-flex items-center px-3 py-1.5 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                  >
                    {isResendingEmail ? 'Sending...' : 'Resend Confirmation'}
                  </button>

                  {order.order.shopifyOrderId && (
                    <button
                      type="button"
                      onClick={onCopyOnboardingLink}
                      className="inline-flex items-center px-3 py-1.5 border border-teal-300 rounded-md text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100"
                    >
                      {linkCopied ? 'Copied!' : 'Copy Onboarding Link'}
                    </button>
                  )}

                  {order.order.pdfId && (
                    <CommonButtonDefault className="text-sm" disabled={disabled} onClick={downloadPdf}>
                      Download PDF
                    </CommonButtonDefault>
                  )}

                  {order.order.status === OrderStatus.Completed && (
                    <button
                      type="button"
                      onClick={onRegeneratePdf}
                      disabled={isRegeneratingPdf}
                      className="inline-flex items-center px-3 py-1.5 border border-amber-300 rounded-md text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50"
                    >
                      {isRegeneratingPdf ? 'Regenerating...' : 'Regenerate PDF'}
                    </button>
                  )}

                  {!order.order.isAsit && (
                    <button
                      type="button"
                      onClick={() => setIsMarking(true)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Set for ASIT
                    </button>
                  )}

                  {order.order.status !== OrderStatus.Completed && order.order.status !== OrderStatus.Cancelled && (
                    <>
                      <button
                        type="button"
                        onClick={() => setIsConfirming(true)}
                        className="inline-flex items-center px-3 py-1.5 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100"
                      >
                        Complete Order
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsCancelling(true)}
                        className="inline-flex items-center px-3 py-1.5 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                      >
                        Cancel Order
                      </button>
                    </>
                  )}

                  {order.order.status === OrderStatus.Completed && (
                    <button
                      type="button"
                      onClick={() => setIsReverting(true)}
                      className="inline-flex items-center px-3 py-1.5 border border-amber-300 rounded-md text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100"
                    >
                      Revert to Sample Received
                    </button>
                  )}

                  {doctors && (
                    <Select
                      className="w-48 text-sm"
                      options={doctors}
                      isClearable
                      placeholder="Assign doctor..."
                      defaultValue={doctors.filter((option) => option.id === order.order.administratorId)}
                      getOptionValue={(option: Allergist) => `${option.id}`}
                      getOptionLabel={(option: Allergist) => `${option.firstName} ${option.lastName}`}
                      onChange={(event) => {
                        if (event) {
                          onSelectDoctor(event as Allergist);
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="mt-3 sm:mt-4 px-4 sm:px-6 lg:px-8">
              <div className="sm:hidden">
                <label htmlFor="current-tab" className="sr-only">
                  Select a tab
                </label>
                <select
                  id="current-tab"
                  name="current-tab"
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  defaultValue={tabs.find((tab) => tab.current)?.name}
                >
                  {order.order.productKey !== 'TREATMENT_PLAN' &&
                    tabs.map((tab) => <option key={`mobile-option-${tab.name}`}>{tab.name}</option>)}
                </select>
              </div>
              <div className="hidden sm:block">
                <nav className="-mb-px flex space-x-8">
                  {order.order.productKey !== 'TREATMENT_PLAN' &&
                    tabs.map((tab) => (
                      <NavLink
                        key={`tab-${tab.name}`}
                        to={tab.href}
                        className={classNames(
                          tab.current
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                          'whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm no-underline',
                        )}
                        aria-current={tab.current ? 'page' : undefined}
                      >
                        {tab.name}
                      </NavLink>
                    ))}
                </nav>
              </div>
            </div>
          </div>

          <div className="px-4 xxl:px-8 mt-8">
            {order.order.productIsTest && order.tests && order.tests.length > 0 && (
              <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      Test (Tyrimai) workflow moved to test menu.{' '}
                      <Link
                        to={`/tests/results/${order.tests[0].id}`}
                        className="font-medium underline text-blue-700 hover:text-blue-600"
                      >
                        View Test #{order.tests[0].id}
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            )}
            <Outlet />
          </div>
        </div>
      )}

      {/* Confirmation Modals */}
      <CommonConfirmationModal
        open={isMarking}
        confirmed={() => onMarkAsitOrder()}
        cancelled={() => setIsMarking(false)}
        title="Are you sure this order is suitable for ASIT?"
        description="This order will be marked as suitable for ASIT."
      />

      <CommonConfirmationModal
        open={isConfirming}
        confirmed={() => onCompleteOrder()}
        cancelled={() => setIsConfirming(false)}
        title="Complete order?"
        description="Email will be sent to customer that result is ready for view."
      />

      <CommonConfirmationModal
        open={isReverting}
        confirmed={() => onRevertOrder()}
        cancelled={() => setIsReverting(false)}
        title="Revert order status?"
        description="This will change the order status back to 'sampleReceived'."
      />

      <CommonConfirmationModal
        open={isCancelling}
        confirmed={() => onCancelOrder()}
        cancelled={() => setIsCancelling(false)}
        title="Cancel order?"
        description="This will cancel the order and any associated tests."
      />

      {/* Patient Management Modal */}
      {order && (
        <PatientManagementModal
          isVisible={isPatientModalVisible}
          onClose={onClosePatientModal}
          onPatientAssigned={onPatientAssigned}
          orderId={order.order.id}
          customerId={order.order.customerId}
        />
      )}
    </div>
  );
}

export default OrdersResultsPageComponent;
